// @ts-strict-ignore
import { useEffect, useRef, useState, useCallback } from 'react';
import type { PropsWithChildren, ReactNode } from 'react';
import { List, ListItem, Alert } from '@mui/material';
import { SwapVert as SwapVertIcon } from '@mui/icons-material';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import useIsMountedRef from '../services/useIsMountedRef.ts';
import { FormLayout } from './FormLayout.tsx';
import { useIntl } from 'react-intl';

export type GenericSortableListProps = {
    title?: ReactNode;
    subTitle?: ReactNode;
    items: ReadonlyArray<{
        id: string;
        label: ReactNode;
    }>;
    open: boolean;
    onClose(): void;
    onSort(ids: ReadonlyArray<string>): Promise<void>;
    isBusy: boolean;
};

export const GenericSortListDialog = (props: GenericSortableListProps) => {
    return (
        <DndProvider backend={HTML5Backend}>
            <GenericSortListDialogInner {...props} />
        </DndProvider>
    );
};
const GenericSortListDialogInner = (props: GenericSortableListProps) => {
    const { formatMessage } = useIntl();
    const {
        open,
        isBusy,
        title = formatMessage({ defaultMessage: 'Sort list' }),
        subTitle = formatMessage({
            defaultMessage:
                'Drag and drop to sort the list. When you\'re done, click "Save".',
        }),
    } = props;
    const [items, setItems] = useState(props.items);
    const [failedToSort, setFailedToSort] = useState(false);
    const isMountedRef = useIsMountedRef();
    const onClickContinue = async () => {
        try {
            const sortedIds = items.map(({ id }) => id);
            await props.onSort(sortedIds);
            props.onClose();
        } catch (e) {
            if (isMountedRef.current) {
                setFailedToSort(true);
            }
            console.error(e);
        }
    };
    const isDirty =
        open && props.items.some((item, index) => items[index]?.id !== item.id);

    // Keep up-to-date when closed
    useEffect(() => {
        if (!open) {
            setItems(props.items);
        }
    }, [props.items, open]);

    const sort = useCallback((from: number, to: number) => {
        setItems((items) => {
            const sortedItems = [...items];
            sortedItems.splice(from, 1);
            sortedItems.splice(to, 0, items[from]);
            return sortedItems;
        });
    }, []);

    return (
        <FormLayout
            mode="dialog"
            dialogProps={{
                open,
            }}
            title={title}
            subTitle={subTitle}
            isValid={true}
            isBusy={isBusy}
            isDirty={isDirty}
            continueButtonProps={{
                onClick: onClickContinue,
            }}
            goBackButtonProps={{
                onClick: props.onClose,
            }}
        >
            <List sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {items.map((item, index) => (
                    <SortableListItem
                        key={item.id}
                        id={item.id}
                        index={index}
                        sort={sort}
                    >
                        <SwapVertIcon
                            sx={(theme) => ({
                                mr: 3,
                                color: theme.palette.text.secondary,
                            })}
                        />
                        {item.label}
                    </SortableListItem>
                ))}
            </List>
            {failedToSort && (
                <Alert severity="error" onClose={() => setFailedToSort(false)}>
                    Failed to sort
                </Alert>
            )}
        </FormLayout>
    );
};

type GenericSortableListItemProps = DragItem &
    PropsWithChildren<{
        sort(fromIndex: number, toIndex: number): void;
    }>;

type DragItem = {
    id: string;
    index: number;
};

const SortableListItem = (props: GenericSortableListItemProps) => {
    const { id, index, sort } = props;
    const ref = useRef<HTMLLIElement>(null);
    const [{ handlerId }, drop] = useDrop({
        accept: 'item',
        collect(monitor) {
            return {
                handlerId: monitor.getHandlerId(),
            };
        },
        hover(item: DragItem, monitor) {
            if (!ref.current) {
                return;
            }
            const dragIndex = item.index;
            const hoverIndex = index;

            // Don't replace items with themselves
            if (dragIndex === hoverIndex) {
                return;
            }

            // Determine rectangle on screen
            const hoverBoundingRect = ref.current?.getBoundingClientRect();

            // Get vertical middle
            const hoverMiddleY =
                (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

            // Determine mouse position
            const clientOffset = monitor.getClientOffset();
            if (!clientOffset) {
                return;
            }

            // Get pixels to the top
            const hoverClientY = clientOffset.y - hoverBoundingRect.top;

            // Only perform the move when the mouse has crossed half of the items height
            // When dragging downwards, only move when the cursor is below 50%
            // When dragging upwards, only move when the cursor is above 50%

            // Dragging downwards
            if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
                return;
            }

            // Dragging upwards
            if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
                return;
            }

            // Time to actually perform the action
            sort(dragIndex, hoverIndex);

            // Note: we're mutating the monitor item here!
            // Generally it's better to avoid mutations,
            // but it's good here for the sake of performance
            // to avoid expensive index searches.
            item.index = hoverIndex;
        },
    });

    const [{ isDragging }, drag] = useDrag({
        type: 'item',
        item: () => ({
            id,
            index,
        }),
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    });

    const opacity = isDragging ? 0 : 1;
    drag(drop(ref));
    return (
        <ListItem
            ref={ref}
            style={{ opacity }}
            data-handler-id={handlerId}
            sx={(theme) => ({
                border: `solid 1px ${theme.bino.color.gray}`,
                borderRadius: 1,
                p: 2,
                cursor: isDragging ? 'grabbing' : 'grab',
            })}
        >
            {props.children}
        </ListItem>
    );
};
